import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Eye, Users } from "lucide-react";
import { getCurrentFacebookPages } from "../../../api/AppLinkApi";

interface Props {
  pageId?: string;
  // Optional: pass engagement payload from /social-posting/engagement/:postId
  engagement?: any;
  platformPostId?: string;
}

export default function FacebookPageInsightsPanel({
  pageId,
  engagement,
  platformPostId: _platformPostId,
}: Props) {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [pages, setPages] = useState<any[]>([]);

  useEffect(() => {
    setIsLoading(true);
    getCurrentFacebookPages()
      .then((data) => setPages(data || []))
      .catch(() => setPages([]))
      .finally(() => setIsLoading(false));
  }, []);

  const page = useMemo(() => {
    if (!pageId) return null;
    return (pages || []).find((p: any) => String(p?.id) === String(pageId)) || null;
  }, [pages, pageId]);

  if (isLoading) {
    return (
      <div className="w-full mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 mt-4 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded w-full" />
            <div className="h-3 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  const toNumber = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // If you pass engagement from /social-posting/engagement/:postId, we can display live post metrics.
  const likeCount = toNumber(engagement?.reactions_like?.summary?.total_count);
  const commentCount = toNumber(engagement?.comments?.summary?.total_count);
  const totalEngagement = likeCount + commentCount;

  return (
    <div className="w-full mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 mt-4">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("Insights for Page")}: {page?.name || t("Unknown")}
        </h3>

        <p className="text-xs text-gray-500 mt-1">
          {t("Insights are fetched live after selecting the Page.")}
        </p>

        {/* Page info section - more organized */}
        <div className="mt-3 space-y-2">
          {/* Page ID with copy button */}
          {pageId ? (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span className="font-medium">{t("Page ID")}:</span>
              <span className="font-mono text-gray-700">{pageId}</span>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-800 transition-colors"
                title={t("Copy")}
                onClick={() => {
                  if (navigator?.clipboard?.writeText) {
                    navigator.clipboard.writeText(String(pageId));
                  }
                }}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* For read_insights: primary requirement is Page selection + label.
          If you pass engagement, we can show live post metrics too (likes/comments).
      */}
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Reach */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">{t("Reach")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">0</div>
            <p className="text-xs text-gray-500 mt-1">{t("Unique accounts that saw this post")}</p>
          </div>

          {/* Impressions */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">{t("Impressions")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">0</div>
            <p className="text-xs text-gray-500 mt-1">
              {t("Total number of times this post was seen")}
            </p>
          </div>

          {/* Engagement */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-gray-700">{t("Engagement")}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {totalEngagement.toLocaleString()}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t("Likes")}: {likeCount.toLocaleString()} • {t("Comments")}:{" "}
              {commentCount.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
